import { useEffect, useRef, useState } from "react";
import {
    Button,
    Col,
    Form,
    Input,
    InputNumber,
    message,
    Popconfirm,
    Row,
    Select,
    Table,
    Tag,
    Tooltip,
} from "antd";
import {
    CloseCircleOutlined,
    DeleteOutlined,
    EditOutlined,
    FormOutlined,
    PlusCircleOutlined,
    ReloadOutlined,
    SaveOutlined,
    SearchOutlined,
    ShopOutlined,
    UnorderedListOutlined,
} from "@ant-design/icons";
import {
    addMaster,
    deleteMaster,
    fetchMaster,
    isDuplicate,
    MASTERS,
    updateMaster,
} from "./Masterapi";
import {
    AMBER,
    EMERALD,
    INDIGO,
    PURPLE,
    ROSE,
    ROW_COLORS,
    SKY,
    TEAL,
    getStyles,
    rowStyles,
} from "./Mastertheme";

const FROM = INDIGO;
const TO = PURPLE;
const G = getStyles(FROM, TO);
const master = MASTERS.item;

const lbl = (text, required) => (
    <span style={{ fontWeight: 600, color: "#374151", fontSize: 13 }}>
        {text} {required && <span style={{ color: ROSE }}>*</span>}
    </span>
);

const clean = (v) => (v === "-" || v == null ? "" : v);
const toOptions = (rows, field) =>
    [...new Set(rows.map((r) => r[field]).filter((v) => v && v !== "-"))].map(
        (v) => ({ value: v, label: v }),
    );

export default function ItemMaster() {
    const [form] = Form.useForm();
    const [tableData, setTableData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editingKey, setEditingKey] = useState(null);
    const [filter, setFilter] = useState("");
    const [lookups, setLookups] = useState({
        brand: [],
        group: [],
        size: [],
        colour: [],
        fitting: [],
        sleeves: [],
    });
    const designRef = useRef(null);

    /* ── fetch ───────────────────────────────────────────────── */
    const fetchData = async () => {
        setLoading(true);
        try {
            setTableData(await fetchMaster(master));
        } catch {
            message.error("Failed to load items");
        } finally {
            setLoading(false);
        }
    };

    const fetchLookups = async () => {
        const keys = Object.keys(lookups);
        const results = await Promise.allSettled(
            keys.map((k) => fetchMaster(MASTERS[k])),
        );
        const next = {};
        keys.forEach((k, i) => {
            next[k] = results[i].status === "fulfilled" ? results[i].value : [];
        });
        setLookups(next);
        if (results.some((r) => r.status === "rejected"))
            message.warning("Some dropdown masters could not be loaded");
    };

    useEffect(() => {
        fetchData();
        fetchLookups();
        setTimeout(() => designRef.current?.focus?.(), 300);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    /* ── derived ─────────────────────────────────────────────── */
    const q = filter.trim().toLowerCase();
    const filteredData = q
        ? tableData.filter((r) =>
            ["designCode", "prodName", "compName", "groupName", "prodSize", "colour"].some(
                (f) => String(r[f] ?? "").toLowerCase().includes(q),
            ),
        )
        : tableData;

    const editingRecord =
        editingKey != null ? tableData.find((r) => r.key === editingKey) : null;

    /* ── handlers ────────────────────────────────────────────── */
    const handleEdit = (r) => {
        setEditingKey(r.key);
        form.setFieldsValue({
            designCode: clean(r.designCode),
            prodName: clean(r.prodName),
            compName: clean(r.compName) || undefined,
            groupName: clean(r.groupName) || undefined,
            hsncode: clean(r.hsncode),
            prodSize: clean(r.prodSize) || undefined,
            colour: clean(r.colour) || undefined,
            fitting: clean(r.fitting) || undefined,
            sleeves: clean(r.sleeves) || undefined,
            disPer: r.disPer,
            dPrice: r.dPrice,
            cPrice: r.cPrice,
            gst: r.gst,
            packpcs: r.packpcs,
            oStock: r.oStock,
        });
        setTimeout(() => designRef.current?.focus?.(), 100);
    };

    const handleCancel = () => {
        form.resetFields();
        setEditingKey(null);
        setFilter("");
        setTimeout(() => designRef.current?.focus?.(), 100);
    };

    const handleRefresh = () => {
        handleCancel();
        fetchData();
        fetchLookups();
    };

    const onGroupChange = (groupName) => {
        const g = lookups.group.find((x) => x.groupName === groupName);
        if (g && g.hsncode && g.hsncode !== "-")
            form.setFieldsValue({ hsncode: g.hsncode });
    };

    const handleSubmit = async (values) => {
        if (isDuplicate(master, tableData, values, editingKey)) {
            message.warning("An item with this design code and name already exists");
            return;
        }
        setSaving(true);
        try {
            if (editingKey != null && editingRecord) {
                await updateMaster(master, editingRecord, values);
            } else {
                await addMaster(master, values);
            }
            message.success(
                editingKey != null ? "Item updated successfully" : "Item added successfully",
                2,
            );
            handleCancel();
            fetchData();
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to save item");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (record) => {
        try {
            await deleteMaster(master, record);
            message.success("Item deleted");
            if (record.key === editingKey) handleCancel();
            fetchData();
        } catch {
            message.error("Delete failed");
        }
    };

    /* ── columns ─────────────────────────────────────────────── */
    const tag = (v, color) =>
        v && v !== "-" ? (
            <Tag
                style={{
                    background: `${color}15`,
                    color,
                    border: `1px solid ${color}44`,
                    borderRadius: 6,
                    fontWeight: 600,
                    fontSize: 12,
                }}
            >
                {v}
            </Tag>
        ) : (
            <span style={{ color: "#cbd5e1", fontSize: 12 }}>—</span>
        );

    const money = (v) => (
        <span style={{ fontWeight: 600, color: "#1e293b", fontSize: 13 }}>
            ₹ {Number(v || 0).toLocaleString("en-IN")}
        </span>
    );

    const columns = [
        {
            title: "#",
            width: 58,
            align: "center",
            render: (_, __, idx) => {
                const color = ROW_COLORS[idx % ROW_COLORS.length];
                return (
                    <span
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 28,
                            height: 28,
                            borderRadius: 8,
                            background: `${color}18`,
                            color,
                            fontWeight: 700,
                            fontSize: 12,
                        }}
                    >
                        {idx + 1}
                    </span>
                );
            },
        },
        {
            title: <span style={{ color: FROM }}>Design Code</span>,
            dataIndex: "designCode",
            width: 130,
            render: (v) => <span style={{ fontWeight: 700, color: "#1e293b" }}>{v}</span>,
        },
        {
            title: <span style={{ color: FROM }}>Item Name</span>,
            dataIndex: "prodName",
            width: 180,
            render: (v) => <span style={{ fontWeight: 600, color: "#1e293b", fontSize: 13 }}>{v}</span>,
        },
        { title: <span style={{ color: SKY }}>Brand</span>, dataIndex: "compName", width: 130, render: (v) => tag(v, SKY) },
        { title: <span style={{ color: PURPLE }}>Category</span>, dataIndex: "groupName", width: 130, render: (v) => tag(v, PURPLE) },
        { title: <span style={{ color: TEAL }}>Size</span>, dataIndex: "prodSize", width: 90, render: (v) => tag(v, TEAL) },
        { title: <span style={{ color: ROSE }}>Colour</span>, dataIndex: "colour", width: 110, render: (v) => tag(v, ROSE) },
        { title: <span style={{ color: AMBER }}>Wholesale</span>, dataIndex: "dPrice", width: 110, align: "right", render: money },
        { title: <span style={{ color: EMERALD }}>Sale</span>, dataIndex: "cPrice", width: 110, align: "right", render: money },
        {
            title: "Actions",
            width: 100,
            align: "center",
            fixed: "right",
            render: (_, record) => (
                <div
                    style={{ display: "flex", gap: 6, justifyContent: "center" }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <Tooltip title="Edit">
                        <Button
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => handleEdit(record)}
                            style={{
                                background: `${INDIGO}15`,
                                color: INDIGO,
                                border: `1px solid ${INDIGO}33`,
                                borderRadius: 8,
                            }}
                        />
                    </Tooltip>
                    <Popconfirm
                        title="Delete this item?"
                        description="This action cannot be undone."
                        onConfirm={() => handleDelete(record)}
                        okText="Delete"
                        cancelText="Cancel"
                        okButtonProps={{ danger: true }}
                    >
                        <Tooltip title="Delete">
                            <Button
                                size="small"
                                icon={<DeleteOutlined />}
                                style={{
                                    background: `${ROSE}15`,
                                    color: ROSE,
                                    border: `1px solid ${ROSE}33`,
                                    borderRadius: 8,
                                }}
                            />
                        </Tooltip>
                    </Popconfirm>
                </div>
            ),
        },
    ];

    const selectProps = (placeholder, options) => ({
        showSearch: true,
        allowClear: true,
        placeholder,
        options,
        optionFilterProp: "label",
        style: { width: "100%", height: 42 },
    });

    const numberProps = {
        min: 0,
        style: { width: "100%", height: 42, borderRadius: 10 },
        controls: false,
    };

    /* ── render ──────────────────────────────────────────────── */
    return (
        <div style={G.page}>
            {/* Header */}
            <div style={G.headerBar}>
                <div style={G.headerLeft}>
                    <div style={G.headerIcon}>
                        <ShopOutlined />
                    </div>
                    <div>
                        <h1 style={G.headerTitle}>Item Master</h1>
                        <p style={G.headerSub}>Manage items with brand, category, size, colour and prices</p>
                    </div>
                </div>
                <div style={G.statsRow}>
                    <div style={G.stat(FROM)}>
                        <span style={G.statNum(FROM)}>{tableData.length}</span>
                        <span style={G.statLabel}>Total</span>
                    </div>
                    <div style={G.stat(SKY)}>
                        <span style={G.statNum(SKY)}>{filteredData.length}</span>
                        <span style={G.statLabel}>Filtered</span>
                    </div>
                </div>
            </div>

            <Row gutter={[20, 20]} align="top">
                {/* Table */}
                <Col xs={24} xl={14}>
                    <div style={G.card}>
                        <div style={G.panelHead(FROM, TO)}>
                            <span style={G.panelTitle}>
                                <UnorderedListOutlined style={{ fontSize: 16 }} />
                                All Items
                            </span>
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    background: "rgba(255,255,255,0.18)",
                                    borderRadius: 20,
                                    padding: "3px 12px 3px 8px",
                                    gap: 6,
                                    minWidth: 220,
                                }}
                            >
                                <SearchOutlined style={{ color: "rgba(255,255,255,0.8)", fontSize: 14 }} />
                                <input
                                    className="mc-search"
                                    value={filter}
                                    onChange={(e) => setFilter(e.target.value)}
                                    placeholder="Search design, item, brand…"
                                    style={{
                                        background: "transparent",
                                        border: "none",
                                        outline: "none",
                                        color: "#fff",
                                        fontSize: 13,
                                        width: "100%",
                                    }}
                                />
                                {filter && (
                                    <CloseCircleOutlined
                                        style={{ color: "rgba(255,255,255,0.7)", cursor: "pointer", fontSize: 13 }}
                                        onClick={() => setFilter("")}
                                    />
                                )}
                            </div>
                        </div>
                        <div style={{ padding: "4px 0" }}>
                            <Table
                                columns={columns}
                                dataSource={filteredData}
                                loading={loading}
                                size="middle"
                                pagination={false}
                                scroll={{ x: 1100, y: "calc(100vh - 320px)" }}
                                rowClassName={(r) => (r.key === editingKey ? "mc-row-selected" : "mc-row")}
                                onRow={(record) => ({
                                    onClick: () => handleEdit(record),
                                    style: { cursor: "pointer" },
                                })}
                            />
                        </div>
                    </div>
                </Col>

                {/* Form */}
                <Col xs={24} xl={10}>
                    <div style={G.card}>
                        <div style={G.panelHead(PURPLE, INDIGO)}>
                            <span style={G.panelTitle}>
                                <FormOutlined style={{ fontSize: 16 }} />
                                {editingKey != null ? "Edit Item" : "Add New Item"}
                            </span>
                            {editingKey != null && (
                                <Tooltip title="Cancel editing">
                                    <button
                                        type="button"
                                        onClick={handleCancel}
                                        style={{
                                            background: "rgba(255,255,255,0.2)",
                                            border: "none",
                                            color: "#fff",
                                            borderRadius: 8,
                                            width: 28,
                                            height: 28,
                                            cursor: "pointer",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontSize: 14,
                                        }}
                                    >
                                        <CloseCircleOutlined />
                                    </button>
                                </Tooltip>
                            )}
                        </div>

                        <div style={G.panelBody}>
                            {editingKey == null ? (
                                <div style={G.hint}>
                                    <PlusCircleOutlined style={{ color: FROM, fontSize: 15 }} />
                                    <span>Fill in the details below to add a new item.</span>
                                </div>
                            ) : (
                                editingRecord && (
                                    <div style={G.selectedBadge(FROM)}>
                                        <div style={G.badgeAvatar(FROM)}>
                                            <ShopOutlined />
                                        </div>
                                        <div>
                                            <div style={{ fontWeight: 700, color: "#1e293b", fontSize: 13 }}>
                                                {editingRecord.prodName}
                                            </div>
                                            <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
                                                Design: {editingRecord.designCode}
                                            </div>
                                        </div>
                                    </div>
                                )
                            )}

                            <Form form={form} layout="vertical" onFinish={handleSubmit} requiredMark={false}>
                                {/* Basic */}
                                <div style={G.sectionLabel(INDIGO)}>
                                    <span style={G.dot(INDIGO)} />
                                    Basic Details
                                </div>
                                <Row gutter={12}>
                                    <Col span={12}>
                                        <Form.Item
                                            label={lbl("Design Code", true)}
                                            name="designCode"
                                            rules={[{ required: true, message: "Design code is required" }]}
                                        >
                                            <Input ref={designRef} placeholder="e.g. D-1001" style={G.inputStyle} />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            label={lbl("Item Name", true)}
                                            name="prodName"
                                            rules={[{ required: true, message: "Item name is required" }]}
                                        >
                                            <Input placeholder="e.g. FORMAL SHIRT" style={G.inputStyle} />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item label={lbl("Brand")} name="compName">
                                            <Select {...selectProps("Select brand", toOptions(lookups.brand, "compName"))} />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item label={lbl("Item Category")} name="groupName">
                                            <Select
                                                {...selectProps("Select category", toOptions(lookups.group, "groupName"))}
                                                onChange={onGroupChange}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item label={lbl("HSN Code")} name="hsncode">
                                            <Input placeholder="Auto from category" style={G.inputStyle} />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                {/* Variants */}
                                <div style={{ ...G.sectionLabel(TEAL), marginTop: 8 }}>
                                    <span style={G.dot(TEAL)} />
                                    Variants
                                </div>
                                <Row gutter={12}>
                                    <Col span={12}>
                                        <Form.Item label={lbl("Size")} name="prodSize">
                                            <Select {...selectProps("Select size", toOptions(lookups.size, "prodSize"))} />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item label={lbl("Colour")} name="colour">
                                            <Select {...selectProps("Select colour", toOptions(lookups.colour, "colour"))} />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item label={lbl("Fitting")} name="fitting">
                                            <Select {...selectProps("Select fitting", toOptions(lookups.fitting, "fitting"))} />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item label={lbl("Sleeves")} name="sleeves">
                                            {/* SlevesMast stores its value under the key "sleves" */}
                                            <Select {...selectProps("Select sleeves", toOptions(lookups.sleeves, "sleves"))} />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                {/* Pricing */}
                                <div style={{ ...G.sectionLabel(AMBER), marginTop: 8 }}>
                                    <span style={G.dot(AMBER)} />
                                    Pricing &amp; Stock
                                </div>
                                <Row gutter={12}>
                                    <Col span={12}>
                                        <Form.Item label={lbl("Wholesale Price")} name="dPrice">
                                            <InputNumber {...numberProps} prefix="₹" placeholder="0" />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item label={lbl("Sale Price")} name="cPrice">
                                            <InputNumber {...numberProps} prefix="₹" placeholder="0" />
                                        </Form.Item>
                                    </Col>
                                    <Col span={8}>
                                        <Form.Item label={lbl("Discount %")} name="disPer">
                                            <InputNumber {...numberProps} max={100} placeholder="0" />
                                        </Form.Item>
                                    </Col>
                                    <Col span={8}>
                                        <Form.Item label={lbl("GST %")} name="gst">
                                            <InputNumber {...numberProps} max={100} placeholder="0" />
                                        </Form.Item>
                                    </Col>
                                    <Col span={8}>
                                        <Form.Item label={lbl("Pack Pcs")} name="packpcs">
                                            <InputNumber {...numberProps} placeholder="0" />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item label={lbl("Opening Stock")} name="oStock">
                                            <InputNumber {...numberProps} placeholder="0" />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <div style={{ display: "flex", gap: 10, marginTop: 6, flexWrap: "wrap" }}>
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        loading={saving}
                                        icon={editingKey != null ? <SaveOutlined /> : <PlusCircleOutlined />}
                                        style={G.primaryBtn}
                                    >
                                        {editingKey != null ? "Update Item" : "Add Item"}
                                    </Button>
                                    {editingKey != null && (
                                        <Button
                                            onClick={handleCancel}
                                            icon={<CloseCircleOutlined />}
                                            style={{
                                                borderRadius: 10,
                                                height: 42,
                                                fontWeight: 600,
                                                border: `1px solid ${ROSE}44`,
                                                color: ROSE,
                                                background: `${ROSE}08`,
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                    )}
                                    <Tooltip title="Refresh — reloads items and dropdowns">
                                        <Button
                                            icon={<ReloadOutlined />}
                                            onClick={handleRefresh}
                                            loading={loading}
                                            style={{
                                                borderRadius: 10,
                                                height: 42,
                                                border: `1px solid ${FROM}44`,
                                                color: FROM,
                                                background: `${FROM}08`,
                                            }}
                                        />
                                    </Tooltip>
                                </div>
                            </Form>
                        </div>
                    </div>
                </Col>
            </Row>

            <style>{rowStyles(FROM)}</style>
        </div>
    );
}